<script setup lang="ts">
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { computed, ref, watch } from 'vue'
import { getWeekBounds } from "./planner.js";
import { toGerman, toIsoLocal } from "@/utils/dateUtils";
import Callout from './Callout.vue'
import DateInput from "./DateInput.vue"
import {useViewStore} from "@/stores/viewStore"

const view = useViewStore();

const props = defineProps<{
  id: string
  target: HTMLElement | null
  show: boolean
  project: any
}>()

const emit = defineEmits(['close', 'printView'])

const startDate = ref(toGerman(props.project.start));
const endDate = ref(toGerman(props.project.end));
const printSelection = ref(2)

const projectMin = computed(() => toGerman(props.project.start))
const projectMax = computed(() => toGerman(props.project.end))

function clampDate(ddmmyyyy: string, min: string, max: string): string {
  const parse = (s: string) => { const [d, m, y] = s.split('-').map(Number); return new Date(y, m - 1, d).getTime(); }
  const t = parse(ddmmyyyy);
  if (t < parse(min)) return min;
  if (t > parse(max)) return max;
  return ddmmyyyy;
}

const startDateModel = computed({
  get() {
    const stored = view.printTimeStart || startDate.value;
    return clampDate(stored, projectMin.value, projectMax.value);
  },
  set(val) {
    startDate.value = val;
    view.setPrintTimeStart(val);
  }
});

const endDateModel = computed({
  get() {
    const stored = view.printTimeEnd || endDate.value;
    return clampDate(stored, projectMin.value, projectMax.value);
  },
  set(val) {
    endDate.value = val;
    view.setPrintTimeEnd(val);
  }
});

function handlePrint() {
  let finalStart = toGerman(props.project.start)
  let finalEnd = toGerman(props.project.end);

  if (printSelection.value === 0) {
    finalStart = toGerman(toIsoLocal(new Date()));
  } else if (printSelection.value === 2) {
    finalStart = startDateModel.value;
    finalEnd = endDateModel.value;
  }

  // Modi 0 + 1: auf Wochengrenzen runden; Individuell: exakte Daten
  if (printSelection.value !== 2) {
    const bounds = getWeekBounds(finalStart, finalEnd)
    finalStart = bounds["earliestMonday"]
    finalEnd = bounds["latestSunday"]
  }

  emit('printView', finalStart, finalEnd);
  emit('close');
}

// Ensure local refs update if the project prop changes
watch(() => props.project, (newP) => {
  startDate.value = toGerman(newP.start);
  endDate.value = toGerman(newP.end);
}, { deep: true });
</script>

<template>
  <Callout :id="id" width="280px" :target="target" :show="show" @close="$emit('close')">
    <div class="flex items-baseline space-x-2 mb-3" @click.stop>
      <span class="px-2 py-0.5 text-xs font-bold rounded text-white" :style="{ backgroundColor: props.project.color }">
        {{ props.project.name }}
      </span>
      <span class="text-sm font-semibold">Personalplan ausgeben</span>
    </div>

    <div class="w-full rounded-md bg-popover" @click.stop>
      <RadioGroup v-model="printSelection" class="flex flex-col space-y-1">
        <div class="flex items-center space-x-2 cursor-pointer rounded-md px-2 py-2 hover:bg-muted" @click="printSelection = 0">
          <RadioGroupItem :value="0" id="opt-0" />
          <Label for="opt-0" class="cursor-pointer">Heute bis Projektende</Label>
        </div>

        <div class="flex items-center space-x-2 cursor-pointer rounded-md px-2 py-2 hover:bg-muted" @click="printSelection = 1">
          <RadioGroupItem :value="1" id="opt-1" />
          <Label for="opt-1" class="cursor-pointer">Projektstart bis Ende</Label>
        </div>

        <div class="flex items-center space-x-2 cursor-pointer rounded-md px-2 py-2 hover:bg-muted" @click="printSelection = 2">
          <RadioGroupItem :value="2" id="opt-2" />
          <Label for="opt-2" class="cursor-pointer">Individuell</Label>
        </div>
      </RadioGroup>

      <div v-if="printSelection === 2" class="date-selection-area mt-4 p-2 border-t border-dashed">
        <p class="text-xs text-gray-500 mb-2">Datumsbereich wählen:</p>
        <div class="bg-gray-100 p-2 rounded flex flex-col gap-2">
          <DateInput
            label="Start"
            :modelValue="startDateModel"
            @datechange="val => {startDateModel = val}"
            :min="projectMin"
            :max="endDateModel"
          />
          <DateInput
            label="Ende"
            :modelValue="endDateModel"
            @datechange="val => {endDateModel = val}"
            :min="startDateModel"
            :max="projectMax"
          />
        </div>
      </div>

      <button 
        class="w-full mt-4 bg-timin text-black py-2 rounded-md text-sm font-medium hover:brightness-90 transition-all"
        @click="handlePrint"
      >
        Druckansicht anzeigen
      </button>
    </div>
  </Callout>
</template>

<style scoped>

/* FIX: Setting a min-height ensures that when the date picker opens 
   or the content changes, the Callout box doesn't jump.
   Adjust 120px to match the height of your two DateInputs.
*/
.date-selection-area {
  min-height: 120px; 
}
</style>